// ============================================================
// AVIRAAPRO ERP - Frontend API Config
// Local/Electron/LAN builds use the configured backend port.
// Public production builds use same-origin reverse proxy routes.
// ============================================================

const DEFAULT_BACKEND_PORT = 8001;

const readBackendPort = (): number => {
  const parsed = Number(process.env.REACT_APP_BACKEND_PORT);
  return Number.isInteger(parsed) && parsed > 0 && parsed < 65536
    ? parsed
    : DEFAULT_BACKEND_PORT;
};

const BACKEND_PORT = readBackendPort();

export const PUBLIC_ROOT_DOMAINS = String(process.env.REACT_APP_PUBLIC_ROOT_DOMAINS || "")
  .split(",")
  .map((value) => value.trim().toLowerCase().replace(/^\.+/, ""))
  .filter(Boolean);
export const PRIMARY_PUBLIC_ROOT_DOMAIN = PUBLIC_ROOT_DOMAINS[0] || "";

type NullableString = string | null | undefined;

const trimSlashes = (value: string): string => value.replace(/\/+$/, "");

export const isPrivateHost = (hostname: NullableString = window.location.hostname): boolean => {
  const host = String(hostname || "").toLowerCase();
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host.startsWith("10.") ||
    host.startsWith("192.168.") ||
    /^172\.(1[6-9]|2\d|3[0-1])\./.test(host)
  );
};

const isPublicWebRuntime = (): boolean => {
  const protocol = window.location.protocol;
  return (protocol === "http:" || protocol === "https:") && !isPrivateHost(window.location.hostname);
};

const normalizeConfiguredBackend = (raw: NullableString): string | null => {
  if (raw === null || raw === undefined) return null;
  const value = String(raw || "").trim();
  if (!value || value === "." || value === "/" || value.toLowerCase() === "same-origin") {
    return "";
  }

  // API_URL represents the backend root. Existing code appends /api itself,
  // so a configured /api suffix is normalized back to same-origin root.
  if (value === "/api") return "";
  if (value.endsWith("/api")) return trimSlashes(value.slice(0, -4));

  return trimSlashes(value);
};

const readStoredBackendUrl = (): string | null => {
  try {
    const stored = localStorage.getItem("ERP_BACKEND_URL");
    if (stored && /^https?:\/\//i.test(stored)) {
      const url = new URL(stored);
      if (isPrivateHost(url.hostname)) {
        url.port = String(BACKEND_PORT);
      }
      return trimSlashes(url.toString());
    }
  } catch (_) {
    // localStorage may be unavailable in hardened browsers.
  }
  return null;
};

const getLocalBackendUrl = (): string => {
  const hostname = window.location.hostname || "localhost";
  const host = hostname === "127.0.0.1" ? "localhost" : hostname;
  return `http://${host}:${BACKEND_PORT}`;
};

const getAPIUrl = (): string => {
  const configured = normalizeConfiguredBackend(process.env.REACT_APP_API_BASE_URL);
  if (configured !== null) return configured;

  const allowStored = !isPublicWebRuntime() || process.env.REACT_APP_ALLOW_STORED_BACKEND_URL === "true";
  if (allowStored) {
    const stored = readStoredBackendUrl();
    if (stored) return stored;
  }

  if (window.location.protocol === "file:" || isPrivateHost(window.location.hostname)) {
    return getLocalBackendUrl();
  }

  // Public cloud path: Nginx serves React and proxies /api + /uploads.
  return "";
};

export function getBackendUrl(): string {
  return getAPIUrl();
}

export function toBackendUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  const base = getBackendUrl();
  return base ? `${base}${cleanPath}` : cleanPath;
}

export const API_URL = getAPIUrl();

export const STORAGE_KEYS = {
  TOKEN: "erp_token",
  REFRESH_TOKEN: "erp_refresh_token",
  USER: "erp_user",
  PERMISSIONS: "erp_permissions",
  TENANT_ID: "erp_tenant_id",
  TENANT_CODE: "erp_tenant_code",
  TENANT_NAME: "erp_tenant_name",
  COMPANY_ID: "erp_company_id",
  COMPANY_CODE: "erp_company_code",
  COMPANY_NAME: "erp_company_name",
  INDUSTRY_TYPE: "erp_industry_type",
} as const;

// Product identity. Company-specific branding comes from company_settings.
export const APP_NAME = "AVIRAAPRO ERP";
export const APP_VERSION = "2.0.0";
export const APP_TAGLINE = "Enterprise Resource Planning";

export default API_URL;
